import { useEffect, useState, useCallback } from 'react';
import { getPolicyDetail, GetPolicyDetailOutputType, publishPolicy, assignPolicy, getPolicyReport, GetPolicyReportOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChevronLeft, Edit2, Send, History, Users, BarChart3, CheckCircle2, Clock, AlertTriangle, Download, Shield, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

type PolicyDetail = GetPolicyDetailOutputType;

interface Props {
  policyId: string;
  onBack: () => void;
  onEdit: (id: string) => void;
  onRefresh: () => void;
}

export default function PolicyDetailView({ policyId, onBack, onEdit, onRefresh }: Props) {
  const [data, setData] = useState<PolicyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [report, setReport] = useState<GetPolicyReportOutputType | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getPolicyDetail({ policyId });
      setData(d);
    } catch { toast.error('Failed to load policy'); }
    setLoading(false);
  }, [policyId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  const p = data.policy;
  const isPublished = p.lifecycleStatus === 'Published';

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-bold text-foreground">{p.policyName}</h2>
            <StatusBadge status={p.lifecycleStatus || 'Draft'} />
            {p.isMandatory && <Badge variant="outline" className="text-xs">Mandatory</Badge>}
          </div>
          {p.policyNumber && <p className="text-sm text-muted-foreground">{p.policyNumber} • Version {p.version || '—'}</p>}
          {p.ownerName && <p className="text-xs text-muted-foreground mt-1">Owner: {p.ownerName}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onEdit(policyId)}><Edit2 className="w-4 h-4 mr-1" />Edit</Button>
          {!isPublished && <Button size="sm" onClick={() => setShowPublish(true)}><Shield className="w-4 h-4 mr-1" />Publish</Button>}
          {isPublished && <Button size="sm" onClick={() => setShowAssign(true)}><Send className="w-4 h-4 mr-1" />Assign</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="versions"><History className="w-4 h-4 mr-1" />Versions ({data.versions.length})</TabsTrigger>
          <TabsTrigger value="assignments"><Users className="w-4 h-4 mr-1" />Assignments ({data.assignments.length})</TabsTrigger>
          <TabsTrigger value="report"><BarChart3 className="w-4 h-4 mr-1" />Report</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <InfoBlock label="Summary" value={p.summary} />
              <InfoBlock label="Effective Date" value={p.effectiveDate ? format(parseISO(p.effectiveDate), 'd MMM yyyy') : '—'} />
              <InfoBlock label="Review Date" value={p.reviewDate ? format(parseISO(p.reviewDate), 'd MMM yyyy') : '—'} />
              <InfoBlock label="Category" value={p.primaryCategoryName || '—'} />
              <InfoBlock label="Departments" value={(p.applicableDepartments || []).join(', ') || '—'} />
              <InfoBlock label="Roles" value={(p.applicableRoles || []).join(', ') || '—'} />
            </div>
            <div className="space-y-4">
              <InfoBlock label="Requires Acknowledgement" value={p.requiresAcknowledgement ? 'Yes' : 'No'} />
              {p.requiresAcknowledgement && <InfoBlock label="Declaration" value={p.declarationWording || '—'} />}
              <InfoBlock label="Source Provenance" value={p.sourceProvenance || '—'} />
              {p.attachmentUrl && <InfoBlock label="Attachment" value={<a href={p.attachmentUrl} target="_blank" rel="noreferrer" className="text-primary underline text-sm">View attachment</a>} />}
            </div>
          </div>
          {(p.policyContent || p.body) && (
            <div className="mt-6 border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2">Policy Content</h3>
              <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap text-sm">{p.policyContent || p.body}</div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="versions">
          <div className="space-y-3">
            {data.versions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No published versions yet.</p>
            ) : data.versions.map(v => (
              <div key={v.id} className="border border-border rounded-lg p-4 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{v.versionLabel}</span>
                    {v.isCurrent && <Badge className="text-[10px]">Current</Badge>}
                    {v.changeType && <Badge variant="outline" className="text-[10px]">{v.changeType}</Badge>}
                  </div>
                  {v.publishedAt && <p className="text-xs text-muted-foreground mt-1">Published {format(parseISO(v.publishedAt), 'd MMM yyyy HH:mm')} by {v.publishedByName}</p>}
                  {v.changeSummary && <p className="text-xs text-muted-foreground mt-1">{v.changeSummary}</p>}
                </div>
                <span className="text-xs text-muted-foreground">{v.ackCount} acks</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assignments">
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted"><tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Employee</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Type</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Deadline</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {data.assignments.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground text-sm">No assignments yet</td></tr>
                ) : data.assignments.map(a => (
                  <tr key={a.id}>
                    <td className="px-4 py-2 text-sm">{a.employeeName || '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground hidden md:table-cell">{a.assignmentType}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground hidden md:table-cell">{a.deadline ? format(parseISO(a.deadline), 'd MMM yyyy') : '—'}</td>
                    <td className="px-4 py-2"><AssignmentStatusBadge status={a.status || 'Pending'} deadline={a.deadline} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="report">
          <ReportTab policyId={policyId} />
        </TabsContent>
      </Tabs>

      {showPublish && <PublishDialog policyId={policyId} onClose={() => setShowPublish(false)} onPublished={() => { setShowPublish(false); load(); onRefresh(); }} />}
      {showAssign && <AssignDialog policyId={policyId} staffList={data.staffList} onClose={() => setShowAssign(false)} onAssigned={() => { setShowAssign(false); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    Draft: 'bg-muted text-muted-foreground', Published: 'bg-primary/10 text-primary',
    'Under Review': 'bg-yellow-500/10 text-yellow-700', Superseded: 'bg-orange-500/10 text-orange-700',
    Withdrawn: 'bg-destructive/10 text-destructive', Archived: 'bg-muted text-muted-foreground',
  };
  return <span className={`text-xs px-2 py-0.5 rounded ${colors[status] || colors.Draft}`}>{status}</span>;
}

function AssignmentStatusBadge({ status, deadline }: { status: string; deadline?: string }) {
  const now = new Date().toISOString().split('T')[0];
  const isOverdue = status === 'Pending' && deadline && deadline < now;
  if (status === 'Acknowledged') return <Badge className="bg-primary/10 text-primary text-xs">Acknowledged</Badge>;
  if (isOverdue) return <Badge variant="destructive" className="text-xs">Overdue</Badge>;
  if (status === 'Cancelled') return <Badge variant="outline" className="text-xs">Cancelled</Badge>;
  if (status === 'Superseded') return <Badge variant="outline" className="text-xs">Superseded</Badge>;
  return <Badge className="bg-yellow-500/10 text-yellow-700 text-xs">Pending</Badge>;
}

function InfoBlock({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="text-sm text-foreground mt-0.5">{value || '—'}</div>
    </div>
  );
}

function PublishDialog({ policyId, onClose, onPublished }: { policyId: string; onClose: () => void; onPublished: () => void }) {
  const [changeSummary, setChangeSummary] = useState('');
  const [changeType, setChangeType] = useState<'Material' | 'Minor' | 'No Ack Required'>('Material');
  const [publishing, setPublishing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await publishPolicy({ policyId, changeSummary, changeType, confirmPublication: true });
      if (!res.success) {
        setErrors(res.validationErrors);
        setPublishing(false);
        return;
      }
      toast.success('Policy published successfully');
      onPublished();
    } catch (e: any) { toast.error(e.message || 'Publication failed'); }
    setPublishing(false);
  };

  // Validate first
  useEffect(() => {
    publishPolicy({ policyId, changeSummary: '', changeType: 'Material', confirmPublication: false })
      .then(r => { if (!r.success) setErrors(r.validationErrors); })
      .catch(() => {});
  }, [policyId]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Publish Policy</DialogTitle></DialogHeader>
        {errors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 space-y-1">
            <p className="text-sm font-semibold text-destructive">Validation errors:</p>
            {errors.map((e, i) => <p key={i} className="text-sm text-destructive flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{e}</p>)}
          </div>
        )}
        <div className="space-y-3">
          <div><Label className="text-sm">Change Type</Label>
            <Select value={changeType} onValueChange={v => setChangeType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Material">Material Change (requires fresh acknowledgement)</SelectItem>
                <SelectItem value="Minor">Minor/Administrative Change</SelectItem>
                <SelectItem value="No Ack Required">No Acknowledgement Required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-sm">Change Summary *</Label>
            <Textarea value={changeSummary} onChange={e => setChangeSummary(e.target.value)} rows={3} placeholder="Describe what changed…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handlePublish} disabled={publishing || !changeSummary.trim() || errors.length > 0}>
            {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}Confirm Publication
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ policyId, staffList, onClose, onAssigned }: { policyId: string; staffList: { id: string; name: string; role?: string; department?: string }[]; onClose: () => void; onAssigned: () => void }) {
  const [assignmentType, setAssignmentType] = useState<'Individual' | 'Role' | 'Department' | 'All Staff'>('All Staff');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState('');
  const [isContinuing, setIsContinuing] = useState(false);
  const [preview, setPreview] = useState<{ id: string; name: string }[] | null>(null);
  const [assigning, setAssigning] = useState(false);

  const handlePreview = async () => {
    try {
      const res = await assignPolicy({
        policyId, assignmentType, deadline, isContinuingRule: isContinuing, confirm: false,
        employeeIds: assignmentType === 'Individual' ? selectedIds : undefined,
        role: assignmentType === 'Role' ? selectedRole : undefined,
        department: assignmentType === 'Department' ? selectedDept : undefined,
      });
      setPreview(res.preview || []);
    } catch (e: any) { toast.error(e.message); }
  };

  const handleConfirm = async () => {
    setAssigning(true);
    try {
      const res = await assignPolicy({
        policyId, assignmentType, deadline, isContinuingRule: isContinuing, confirm: true,
        employeeIds: assignmentType === 'Individual' ? selectedIds : undefined,
        role: assignmentType === 'Role' ? selectedRole : undefined,
        department: assignmentType === 'Department' ? selectedDept : undefined,
      });
      toast.success(`Assigned to ${res.assignedCount} employees${res.skippedDuplicates > 0 ? ` (${res.skippedDuplicates} already assigned)` : ''}`);
      onAssigned();
    } catch (e: any) { toast.error(e.message); }
    setAssigning(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Assign Policy</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Assignment Type</Label>
            <Select value={assignmentType} onValueChange={v => { setAssignmentType(v as any); setPreview(null); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All Staff">All Active Employees</SelectItem>
                <SelectItem value="Department">By Department</SelectItem>
                <SelectItem value="Role">By Role</SelectItem>
                <SelectItem value="Individual">Individual Employees</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {assignmentType === 'Role' && (
            <div><Label>Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {['Trainee','Staff','Supervisor','Manager','Admin'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {assignmentType === 'Department' && (
            <div><Label>Department</Label>
              <Select value={selectedDept} onValueChange={setSelectedDept}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {['Front of House','Back of House','Kitchen','Management'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {assignmentType === 'Individual' && (
            <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
              {staffList.map(s => (
                <label key={s.id} className="flex items-center gap-2 text-sm p-1 hover:bg-muted rounded">
                  <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={e => {
                    setSelectedIds(e.target.checked ? [...selectedIds, s.id] : selectedIds.filter(x => x !== s.id));
                    setPreview(null);
                  }} />{s.name} <span className="text-xs text-muted-foreground">({s.role})</span>
                </label>
              ))}
            </div>
          )}
          <div><Label>Deadline</Label><Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={isContinuing} onCheckedChange={setIsContinuing} />
            <Label className="text-sm">Continuing rule (auto-assign to new starters)</Label>
          </div>
          {!preview && <Button variant="outline" onClick={handlePreview} className="w-full">Preview Affected Employees</Button>}
          {preview && (
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm font-semibold mb-2">{preview.length} employees will be assigned:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {preview.map(e => <p key={e.id} className="text-sm">{e.name}</p>)}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={assigning || !preview}>
            {assigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportTab({ policyId }: { policyId: string }) {
  const [report, setReport] = useState<GetPolicyReportOutputType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPolicyReport({ policyId }).then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [policyId]);

  if (loading) return <Skeleton className="h-32" />;
  if (!report) return <p className="text-sm text-muted-foreground">Failed to load report.</p>;

  const pStats = report.policies[0];
  const handleExport = async () => {
    const res = await getPolicyReport({ policyId, exportCsv: true });
    if (res.csvData) {
      const blob = new Blob([res.csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `policy-report-${policyId}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    }
  };

  return (
    <div>
      {pStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-xs text-muted-foreground">Assigned</p><p className="text-xl font-bold">{pStats.assignedCount}</p>
          </div>
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-xs text-muted-foreground">Acknowledged</p><p className="text-xl font-bold text-primary">{pStats.acknowledgedCount}</p>
          </div>
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-xs text-muted-foreground">Outstanding</p><p className="text-xl font-bold">{pStats.outstandingCount}</p>
          </div>
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-xs text-muted-foreground">Overdue</p><p className="text-xl font-bold text-destructive">{pStats.overdueCount}</p>
          </div>
        </div>
      )}
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="outline" onClick={handleExport}><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Employee</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Role</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Dept</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Deadline</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {report.employees.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground text-sm">No assignments</td></tr>
            ) : report.employees.map(e => (
              <tr key={e.id}>
                <td className="px-3 py-2 text-sm">{e.name}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell">{e.role || '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell">{e.department || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${e.status === 'Acknowledged' ? 'bg-primary/10 text-primary' : e.status === 'Overdue' ? 'bg-destructive/10 text-destructive' : 'bg-yellow-500/10 text-yellow-700'}`}>{e.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell">{e.deadline ? format(parseISO(e.deadline), 'd MMM yyyy') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
